import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BillingLineKind } from '@treadinc/horizon-api-spec';
import { useContext } from 'react';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import useAiReviewDismissal from '~hooks/useAiReview/useAiReviewDismissal';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import LoadReviewIndicator from '~pages/Approvals/ApprovalsComponents/LoadReviewIndicator';
import LoadsQueryContext from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import { ApprovalActions } from '~pages/Approvals/Files/components/LoadsTable/Columns/ActionsColumn';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { LoadCharges } from '../groupBillingLines';
import BillingLineActionsMenu from './BillingLineActionsMenu';
import ChargesRemoveLoadButton from './ChargesRemoveLoadButton';

const JOB_ID_COPIED_ALERT_DURATION_IN_MS = 1000;
const JOB_ID_TEXT_MAX_HEIGHT = '10px';

interface ChargesLoadActionsProps {
  charges?: LoadCharges;
  job: Job;
  load: Load;
  ticket?: Ticket;
}

export default function ChargesLoadActions({
  charges,
  job,
  load,
  ticket,
}: ChargesLoadActionsProps) {
  const { userStore } = useStores();
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const { refreshLoads } = useContext(LoadsQueryContext);
  const companyId = job.company?.id ?? getEffectiveUserCompanyId(userStore);
  const { isApproved, isApprovable, isUnapprovable } = getApprovalStatus(
    load,
    userStore.getPermissions(),
    Boolean(job.order?.editable),
  );
  const { dismiss: handleDismissAiReview, undismiss: handleUndoAiReviewDismissal } =
    useAiReviewDismissal({ companyId, load, onChanged: refreshLoads });
  // The per-load add-ons are acted on from their own rows below the loads
  const freightLines = (charges?.lines ?? []).filter(
    (line) => line.kind !== BillingLineKind.ADD_ON_CHARGE,
  );

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        alignItems: 'flex-start',
        display: 'flex',
        gap: 0.5,
        justifyContent: 'flex-end',
      }}
    >
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <ApprovalActions
            companyId={companyId}
            isApprovable={isApprovable}
            isApproved={isApproved}
            isUnapprovable={isUnapprovable}
            load={load}
            onApprovalChanged={reload}
          />

          <LoadReviewIndicator
            aiReview={load.aiReview}
            canDismiss={isApprovable || isApproved}
            isTicketFlagged={Boolean(ticket?.flagged)}
            onDismiss={handleDismissAiReview}
            onUndoDismiss={handleUndoAiReviewDismissal}
          />
        </Box>

        {job.jobId && (
          <CopyToClipboard
            alertMessageDuration={JOB_ID_COPIED_ALERT_DURATION_IN_MS}
            hideTextWhileCopying
            sx={{ maxHeight: JOB_ID_TEXT_MAX_HEIGHT }}
            textToCopy={job.jobId}
          >
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '10px',
              })}
            >
              {job.jobId}
            </Typography>
          </CopyToClipboard>
        )}
      </Box>

      <BillingLineActionsMenu
        isLoadApproved={isApproved}
        lines={freightLines}
        loadId={load.id}
        loadLines={charges?.lines ?? []}
        ticket={ticket}
      />

      <ChargesRemoveLoadButton
        isLoadApproved={isApproved}
        job={job}
        lines={charges?.lines ?? []}
        load={load}
        ticket={ticket}
      />
    </Box>
  );
}
